import {
  VISUAL_COMPONENT_BACKGROUNDS,
  VISUAL_COMPONENT_BORDERS,
  VISUAL_COMPONENT_HIGHLIGHTED_BACKGROUNDS,
  VISUAL_COMPONENT_TEXT,
} from "@/domains/colors/colorsets"
import { ColorSet } from "@/domains/colors/types"
import { BodyCaption } from "@/domains/design/components/body-caption"
import { BodyXs } from "@/domains/design/components/body-xs"
import { MoveRightIcon } from "@/domains/icons"
import { cn, formatCurrency } from "@/domains/library"
import type { LockDisplayData } from "@/domains/lock-page/components/card/0.story-mocks"
import type { TokenStore } from "@/domains/tokens"

const getColorFromSet = (diff: number, isScalest: boolean, colorSet: ColorSet) => {
  if (diff === 0) return colorSet.default
  return isScalest ? colorSet.scalest : colorSet.scalar
}

export const LockTargetVisualizer = ({
  currentLock,
  targetAmount,
  amountToAdd,
  governanceToken,
}: {
  currentLock: LockDisplayData | undefined
  targetAmount: string
  amountToAdd: string
  governanceToken: TokenStore
}) => {
  if (!currentLock) return null

  const targetAmountNum = Number(targetAmount) || 0
  const amountToAddNum = Number(amountToAdd) || 0
  const currentLockAmount = currentLock.lockAmount

  // Determine if we have a valid target
  const hasValidTarget = targetAmountNum > 0 && targetAmountNum > currentLockAmount

  // When no valid target, show only current lock (use current as "target" for display)
  const displayTarget = hasValidTarget ? targetAmountNum : currentLockAmount
  const displayAmountToAdd = hasValidTarget ? amountToAddNum : 0

  // Calculate percentages for three segments
  // When no valid target, show 100% locked (no added or target segments)
  const lockedP = hasValidTarget ? (currentLockAmount / displayTarget) * 100 : 100
  const addedP = hasValidTarget ? (displayAmountToAdd / displayTarget) * 100 : 0
  const targetP = hasValidTarget ? 100 - lockedP - addedP : 0

  // Calculate veSCALE values
  const currentPowerRatio = currentLockAmount > 0 ? currentLock.lockValue / currentLockAmount : 0
  const targetLockValue = displayTarget * currentPowerRatio
  const addedLockValue = displayAmountToAdd * currentPowerRatio

  // Calculate percentage increase for color coding
  const percentageIncrease = displayAmountToAdd > 0 ? displayAmountToAdd / currentLockAmount : 0

  const currentBg = getColorFromSet(percentageIncrease, currentLock.isScalest, VISUAL_COMPONENT_BACKGROUNDS)
  const addedBg = getColorFromSet(percentageIncrease, currentLock.isScalest, VISUAL_COMPONENT_HIGHLIGHTED_BACKGROUNDS)
  const targetBg = getColorFromSet(percentageIncrease, currentLock.isScalest, VISUAL_COMPONENT_BACKGROUNDS)
  const border = getColorFromSet(percentageIncrease, currentLock.isScalest, VISUAL_COMPONENT_BORDERS)
  const innerText = getColorFromSet(percentageIncrease, currentLock.isScalest, VISUAL_COMPONENT_TEXT)

  return (
    <div>
      <div className="mb-1 flex flex-row items-center justify-between px-1">
        <BodyCaption As="div" className="text-t300">
          {formatCurrency(currentLockAmount)} Locked
        </BodyCaption>
        {hasValidTarget && (
          <>
            <BodyCaption As="div" className="text-t300">
              <div className="flex flex-row items-center gap-1">
                <MoveRightIcon size={14} />
                {formatCurrency(displayAmountToAdd)} Added
              </div>
            </BodyCaption>
            <BodyCaption As="div" className="text-t300">
              <div className="flex flex-row items-center gap-1">
                <MoveRightIcon size={14} />
                {formatCurrency(displayTarget)} Target
              </div>
            </BodyCaption>
          </>
        )}
        {!hasValidTarget && (
          <BodyCaption As="div" className="text-t300">
            {formatCurrency(currentLockAmount)} Locked
          </BodyCaption>
        )}
      </div>
      <div
        className={cn(
          "relative flex h-[32px] w-full flex-row items-center justify-between",
          "overflow-hidden rounded-xl",
          "border border-p100-light",
          "bg-p300",
          border,
        )}
      >
        {/* Locked segment (left) */}
        <div
          style={{ width: `${lockedP}%` }}
          className={cn("absolute left-0 top-0 z-0 h-[32px] py-2 text-center", currentBg)}
        ></div>
        {/* Added segment (middle) */}
        <div
          style={{ width: `${addedP}%`, left: `${lockedP}%` }}
          className={cn(
            "absolute top-0 z-0 h-[32px] py-2 text-center",
            amountToAddNum > 0 && "border-l-2 border-r-2 border-l-white/20 border-r-white/20",
            addedBg,
          )}
        ></div>
        {/* Target segment (right) - this is the remaining space to reach target */}
        <div
          style={{ width: `${targetP}%`, left: `${lockedP + addedP}%` }}
          className={cn("absolute top-0 z-0 h-[32px] py-2 text-center", targetBg)}
        ></div>

        <BodyXs As="div" className={cn("z-10 flex w-full items-center justify-center", innerText)}>
          {hasValidTarget ? `${formatCurrency(displayAmountToAdd)} Added` : formatCurrency(currentLockAmount)}
        </BodyXs>
      </div>
      <div className="mt-1 flex flex-row items-center justify-between px-1">
        <BodyCaption As="div" className="text-t300">
          {formatCurrency(currentLock.lockValue)} veSCALE
        </BodyCaption>

        {hasValidTarget && (
          <>
            <BodyCaption As="div" className="text-t300">
              <div className="flex flex-row items-center gap-1">
                <MoveRightIcon size={14} />
                {formatCurrency(addedLockValue)} Added
              </div>
            </BodyCaption>
            <BodyCaption As="div" className="text-t300">
              <div className="flex flex-row items-center gap-1">
                <MoveRightIcon size={14} />
                {formatCurrency(targetLockValue)} veSCALE
              </div>
            </BodyCaption>
          </>
        )}
        {!hasValidTarget && (
          <BodyCaption As="div" className="text-t300">
            {formatCurrency(currentLock.lockValue)} veSCALE
          </BodyCaption>
        )}
      </div>
    </div>
  )
}
